import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Footer } from "@/components/rentalmnp/shared/Footer";
import { Logo } from "@/components/rentalmnp/shared/Logo";

const TITLE = "Méthodologie de calcul du simulateur LMNP";
const DESC =
  "Transparence totale : découvrez les formules de calcul exactes de notre simulateur LMNP. Rentabilité brute, nette, nette-nette, cash-flow détaillés.";
const URL = "https://rentalmnp.com/methodologie";

export const Route = createFileRoute("/methodologie")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: MethodoPage,
});

function MethodoPage() {
  return (
    <div className="min-h-screen bg-background">
      <main className="mx-auto max-w-4xl px-6 py-8 lg:px-12">
        <div className="mb-6 flex items-center justify-between gap-3">
          <Logo size={36} />
          <Link to="/" className="inline-flex shrink-0 items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-primary">
            <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">Retour à l'accueil</span><span className="sm:hidden">Accueil</span>
          </Link>
        </div>

        <header className="mt-6 mb-10">
          <h1 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            Comment nous calculons la rentabilité de votre LMNP
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Transparence totale sur les formules et les hypothèses utilisées par le
            simulateur RentaLMNP.
          </p>
        </header>

        <section className="prose prose-slate max-w-none space-y-8">
          <div>
            <h2 className="text-2xl font-bold text-foreground">1. Rentabilité brute</h2>
            <p className="text-muted-foreground">
              C'est l'indicateur le plus simple — et le plus trompeur s'il est utilisé seul.
            </p>
            <pre className="mt-3 whitespace-pre-wrap break-words rounded-lg bg-secondary p-4 text-xs text-foreground sm:text-sm">
{`Rentabilité brute = (Loyer mensuel × 12) / Prix d'acquisition × 100`}
            </pre>
            <p className="mt-3 text-sm text-muted-foreground">
              <strong>Exemple :</strong> studio à 150 000 €, loué 600 €/mois →
              (600 × 12) / 150 000 = <strong>4,8 % brut</strong>.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-foreground">2. Rentabilité nette de charges</h2>
            <p className="text-muted-foreground">
              On retranche les charges récurrentes (taxe foncière, assurance PNO, charges de
              copropriété non récupérables, entretien, CFE, frais de gestion).
            </p>
            <pre className="mt-3 whitespace-pre-wrap break-words rounded-lg bg-secondary p-4 text-xs text-foreground sm:text-sm">
{`Net = (Loyers annuels × (1 - vacance) - Charges) / Prix total × 100

Prix total = Prix d'acquisition + Travaux + Mobilier`}
            </pre>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-foreground">3. Rentabilité nette-nette (après impôts)</h2>
            <p className="text-muted-foreground">
              On intègre la fiscalité selon le régime choisi (micro-BIC ou réel) et votre TMI.
              Pour le régime réel, on calcule les amortissements selon les règles fiscales :
            </p>
            <ul className="mt-3 list-disc pl-6 text-sm text-muted-foreground">
              <li><strong>Bien immobilier</strong> : ~85 % du prix amortis sur 25 à 30 ans</li>
              <li><strong>Mobilier</strong> : amorti sur 7 ans</li>
              <li><strong>Travaux</strong> : amortis sur 10 ans</li>
              <li><strong>Intérêts d'emprunt</strong> : déductibles à 100 % (régime réel)</li>
            </ul>
            <p className="mt-3 text-muted-foreground">
              Le résultat fiscal négatif (déficit) est reportable 10 ans en LMNP au réel.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-foreground">4. Cash-flow mensuel</h2>
            <pre className="mt-3 whitespace-pre-wrap break-words rounded-lg bg-secondary p-4 text-xs text-foreground sm:text-sm">
{`Cash-flow = Loyer net - Mensualité crédit - Charges mensuelles - Impôt mensualisé`}
            </pre>
            <p className="mt-3 text-sm text-muted-foreground">
              C'est l'indicateur clé : un cash-flow positif signifie que votre bien
              s'autofinance et génère du revenu chaque mois.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-foreground">Limites du simulateur</h2>
            <ul className="mt-3 list-disc space-y-2 pl-6 text-muted-foreground">
              <li>Les résultats sont des <strong>estimations basées sur les règles fiscales LMNP 2026</strong> (loi de finances 2026 votée fin 2025).</li>
              <li>Nous ne tenons pas compte des évolutions législatives futures (loi de finances 2027, etc.).</li>
              <li>Les amortissements appliqués sont des moyennes ; un expert-comptable peut affiner selon le bien.</li>
              <li>Le simulateur ne remplace pas un conseil fiscal personnalisé.</li>
            </ul>
          </div>

          <div className="rounded-2xl border border-primary/20 bg-primary/5 p-6">
            <h3 className="text-lg font-bold text-foreground">Prêt à tester votre projet ?</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Le simulateur applique automatiquement toutes ces formules à vos chiffres.
            </p>
            <Link to="/" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground hover:-translate-y-0.5 hover:bg-primary-dark transition">
              Faire une simulation LMNP <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
